import type { RumEvent } from '@datadog/browser-rum';
import { currentUser } from '#api/auth.ts';
import type { DatadogRumConfig } from '#api/utility.ts';

let started = false;

export async function startDatadogRum(config: DatadogRumConfig | null | undefined, version: string | undefined) {
    if (started || !config) return;
    started = true;

    let datadogRum: typeof import('@datadog/browser-rum').datadogRum;
    try {
        ({ datadogRum } = await import('@datadog/browser-rum'));
    } catch {
        started = false;
        return;
    }

    currentUser.subscribe((user) => {
        if (!user) {
            datadogRum.clearUser();
            return;
        }

        datadogRum.setUser({
            id: String(user.id),
            clientId: user.clientId,
            isObligor: user.isObligor,
            isGlobal: user.isGlobal,
            isImpersonating: user.isImpersonating
        });
    });

    datadogRum.init({
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        site: 'us5.datadoghq.com',
        service: 'link-spa',
        env: config.env ?? undefined,
        version: version?.replace(/[^\w.:/-]/g, '_'),
        sessionSampleRate: config.sessionSampleRate,
        traceSampleRate: config.traceSampleRate,
        sessionReplaySampleRate: 0,
        defaultPrivacyLevel: 'mask',
        enablePrivacyForActionName: true,
        allowedTracingUrls: [window.location.origin],
        beforeSend
    });
}

export function beforeSend(event: RumEvent) {
    if (event.type === 'error' && event.error.source === 'console') return false;

    event.view.url = stripQuery(event.view.url);
    if (event.view.referrer) event.view.referrer = stripQuery(event.view.referrer);
    if (event.type === 'resource') event.resource.url = stripQuery(event.resource.url);
    if (event.type === 'error') {
        event.error.message = stripQueriesInText(event.error.message);
        if (event.error.stack) event.error.stack = stripQueriesInText(event.error.stack);
        if (event.error.resource) event.error.resource.url = stripQuery(event.error.resource.url);
    }

    return true;
}

function stripQuery(url: string) {
    const end = url.search(/[?#]/);
    return end === -1 ? url : url.slice(0, end);
}

function stripQueriesInText(text: string) {
    return text.replace(/((?:https?:\/\/|\/)[^\s?#'"]*)[?#][^\s'"]*/g, '$1');
}
